
import React, { useState, useEffect, useRef, useCallback } from 'react';
import cogoToast from 'cogo-toast';
import Modal from '../../../components/UI/ModalTailwind';
import AbonarForm from './AbonarForm';
import {
    getInquilinosBalance,
    setAbono,
    getViviendasBySubfraccionamiento,
    getInquilinosPorVivienda
} from '../requests';
import { getMetodosPago } from '../../../helpers/requests';

export default function AbonarGeneralModal({ isOpen, onClose, onAbonoSuccess }) {
    const [loading, setLoading] = useState(false);
    const [subfraccionamientos, setSubfraccionamientos] = useState([]);
    const [viviendas, setViviendas] = useState([]);
    const [inquilinos, setInquilinos] = useState([]);
    const [metodosPago, setMetodosPago] = useState([]);

    const [selectedSub, setSelectedSub] = useState('');
    const [selectedViv, setSelectedViv] = useState('');
    const [selectedInquilino, setSelectedInquilino] = useState(null);

    const [loadingAbono, setLoadingAbono] = useState(false);
    const formRef = useRef();

    useEffect(() => {
        if (!isOpen) {
            setSelectedSub('');
            setSelectedViv('');
            setSelectedInquilino(null);
            setViviendas([]);
            setInquilinos([]);
        }
    }, [isOpen]);

    useEffect(() => {
        const loadData = async () => {
            if (!isOpen) return;
            setLoading(true);
            try {
                const balanceRes = await getInquilinosBalance(localStorage.getItem('frac'), 0);
                if (balanceRes.balances) {
                    const subsMap = {};
                    balanceRes.balances.forEach(r => {
                        if (r.id_subfraccionamiento && r.subfraccionamiento) {
                            subsMap[r.id_subfraccionamiento] = r.subfraccionamiento;
                        }
                    });
                    const subs = Object.keys(subsMap).map(id => ({
                        id: Number(id),
                        nombre: subsMap[id]
                    }));
                    setSubfraccionamientos(subs);
                } else {
                    cogoToast.warn('No se encontraron subfraccionamientos', { position: 'bottom-right' });
                }

                const pagoRes = await getMetodosPago();
                if (pagoRes.metodosPago) {
                    const filtrados = pagoRes.metodosPago.filter(m => m.Id_FormaPago !== -1);
                    setMetodosPago(filtrados);
                }
            } catch (error) {
                cogoToast.error('Error al cargar datos iniciales', { position: 'bottom-right' });
            }
            setLoading(false);
        };
        loadData();
    }, [isOpen]);

    // Cascada: subfraccionamiento → viviendas
    useEffect(() => {
        const fetchViviendas = async () => {
            if (!selectedSub) {
                setViviendas([]);
                setSelectedViv('');
                setInquilinos([]);
                setSelectedInquilino(null);
                return;
            }
            setLoading(true);
            const response = await getViviendasBySubfraccionamiento(
                localStorage.getItem('frac'),
                Number(selectedSub)
            );
            if (response.error) {
                cogoToast.error(response.error, { position: 'bottom-right' });
                setViviendas([]);
            } else {
                setViviendas(response.viviendas || []);
            }
            setSelectedViv('');
            setInquilinos([]);
            setSelectedInquilino(null);
            setLoading(false);
        };
        fetchViviendas();
    }, [selectedSub]);

    // Cascada: vivienda → inquilinos
    useEffect(() => {
        const fetchInquilinos = async () => {
            if (!selectedViv) {
                setInquilinos([]);
                setSelectedInquilino(null);
                return;
            }
            setLoading(true);
            const response = await getInquilinosPorVivienda(
                Number(selectedViv),
                localStorage.getItem('frac')
            );
            if (response.error) {
                cogoToast.error(response.error, { position: 'bottom-right' });
                setInquilinos([]);
            } else {
                setInquilinos(response.inquilinos || []);
            }
            setSelectedInquilino(null);
            setLoading(false);
        };
        fetchInquilinos();
    }, [selectedViv]);

    const handleAbonoSubmit = useCallback(async (formData) => {
        setLoadingAbono(true);
        const response = await setAbono(formData);
        setLoadingAbono(false);
        if (response.error) {
            cogoToast.error(response.error, { position: 'bottom-right' });
            return;
        }
        if (response.created) {
            cogoToast.success('Movimiento realizado', { position: 'bottom-right' });
            onClose();
            if (onAbonoSuccess) onAbonoSuccess();
        }
    }, [onClose, onAbonoSuccess]);

    const handleSubmit = () => {
        if (formRef.current && typeof formRef.current.submit === 'function') {
            formRef.current.submit();
        } else {
            cogoToast.error('Error al enviar el formulario', { position: 'bottom-right' });
        }
    };

    const subNombre = subfraccionamientos.find(s => s.id === Number(selectedSub))?.nombre || '';
    const viviendaNumero = viviendas.find(v => v.id_vivienda === Number(selectedViv))?.numero_registro || '';
    const inquilinoNombre = selectedInquilino?.nombre || selectedInquilino?.inquilino || '';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Nuevo Abono"
            size="md"
            submitLabel="ABONAR"
            showSubmit={!!selectedInquilino}
            loading={loading || loadingAbono}
            disabled={!selectedInquilino}
            onSubmit={handleSubmit}
        >
            <div className="space-y-4">
                {/* Selectores en cascada */}
                <div className="space-y-4 p-7">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Subfraccionamiento
                        </label>
                        <select
                            className="input-edit-select-full text-left" // ← usando tu clase personalizada
                            value={selectedSub}
                            onChange={e => setSelectedSub(e.target.value ? Number(e.target.value) : '')}
                            disabled={loading}
                        >
                            <option value="">Todos</option>
                            {subfraccionamientos.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vivienda
                        </label>
                        <select
                            className="input-edit-select-full text-left"
                            value={selectedViv}
                            onChange={e => setSelectedViv(e.target.value)}
                            disabled={!selectedSub || loading}
                        >
                            <option value="">Seleccione</option>
                            {viviendas.map(v => (
                                <option key={v.id_vivienda} value={v.id_vivienda}>
                                    {v.numero_registro}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Residente
                        </label>
                        <select
                            className="input-edit-select-full text-left"
                            value={selectedInquilino?.id_inquilino || ''}
                            onChange={e => {
                                const id = parseInt(e.target.value);
                                const inquilino = inquilinos.find(r => r.id_inquilino === id);
                                setSelectedInquilino(inquilino || null);
                            }}
                            disabled={!selectedViv || loading}
                        >
                            <option value="">Seleccione</option>
                            {inquilinos.map(r => (
                                <option key={r.id_inquilino} value={r.id_inquilino}>
                                    {r.nombre || r.inquilino}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <hr className="border-t border-gray-200 my-4" />

                {/* Formulario de abono */}
                <AbonarForm
                    ref={formRef}
                    numero_registro={viviendaNumero}
                    subfraccionamiento={subNombre}
                    inquilino={inquilinoNombre}
                    id_inquilino={selectedInquilino?.id_inquilino || 0}
                    metodosPago={metodosPago}
                    loadingInquilinos={loadingAbono}
                    onSubmit={handleAbonoSubmit}
                    simple={true}
                />
            </div>
        </Modal>
    );
}